import posthog from 'posthog-js';
import { resetSessionAnalytics } from './sessionAnalytics';
import { requiredSsoUrl, startSsoSignIn } from '../../util/ssoSignIn';

// When the last session this tab was sent to single sign-on for was refused again, so a server
// that keeps refusing SSO sessions sends the user to the login page instead of round a loop.
const SSO_REFUSAL_KEY = 'm8_sso_refused_at';
const SSO_REFUSAL_WINDOW_MS = 2 * 60 * 1000;

// The page this tab is leaving for single sign-on: requests refused alongside the first one must
// not reload it and cancel that navigation. window.location is one object for a page's lifetime.
let leavingForSsoFrom = null;

const safely = (callback) => {
  try {
    callback();
  } catch {
    // Session invalidation must continue even when browser storage or telemetry is unavailable.
  }
};

const recentlySentToSso = () => {
  try {
    const at = Number(sessionStorage.getItem(SSO_REFUSAL_KEY));
    return Number.isFinite(at) && at > 0 && Date.now() - at < SSO_REFUSAL_WINDOW_MS;
  } catch {
    return false;
  }
};

// The page to come back to after signing in again through single sign-on.
const currentAppPath = () => {
  const { pathname, search } = window.location;
  return pathname.startsWith('/app') ? `${pathname}${search}` : 'null';
};

const redirectToLogin = (dispatch, error) => {
  if (leavingForSsoFrom === window.location) return;

  safely(() => dispatch?.({ type: 'INVALID' }));

  // The organization requires single sign-on and this session did not come through it
  // (SamlSso::SessionPolicy): sign in there instead of at the password login.
  const ssoUrl = requiredSsoUrl(error);
  const followSso = ssoUrl && !recentlySentToSso();

  const forceRefresh = error?.response?.data?.force || false;
  if (!followSso) {
    safely(() => localStorage.setItem(
      'm8_error',
      forceRefresh || ssoUrl
        ? 'Your session has expired. Please login again to continue'
        : 'Invalid username or password',
    ));
  }

  if (process.env.NODE_ENV !== 'development' && posthog) {
    safely(() => posthog.reset());
  }
  resetSessionAnalytics();

  if (followSso) {
    safely(() => sessionStorage.setItem(SSO_REFUSAL_KEY, String(Date.now())));
    if (startSsoSignIn(ssoUrl, { returnPath: currentAppPath() })) {
      leavingForSsoFrom = window.location;
      return;
    }
  }
  safely(() => window.location.reload());
};

export default redirectToLogin;
